import { withProps } from "@udecode/cn";
import { BaseParagraphPlugin, TElement, isElementList } from "@udecode/plate";
import {
    BoldPlugin,
    Plate,
    PlateElement,
    PlateLeaf,
    createPlatePlugin,
    usePlateEditor,
} from "@udecode/plate/react";
import { BoldIcon, Heading1 } from "lucide-react";

import ToggleBlockToolbarButton from "@/components/RichTextEditor/ToggleBlockToolbarButton";
import { Editor, EditorContainer } from "@/components/plate-ui/editor";
import { FixedToolbar } from "@/components/plate-ui/fixed-toolbar";
import { MarkToolbarButton } from "@/components/plate-ui/mark-toolbar-button";
import { ParagraphElement } from "@/components/plate-ui/paragraph-element";
import { ToolbarGroup } from "@/components/plate-ui/toolbar";

const EMPTY_EDITOR_CONTENT = [
    {
        type: "paragraph",
        children: [{ text: "" }],
    },
];

const ParagraphPlugin = BaseParagraphPlugin.configure({
    node: {
        isElement: true,
        type: "paragraph",
        component: ParagraphElement,
    },
});

const HeadingPlugin = createPlatePlugin({
    key: "heading",
    node: {
        isElement: true,
        type: "heading",
        component: withProps(PlateElement, {
            as: "h1",
            className: "font-planar15 text-lg",
        }),
    },
    shortcuts: {
        toggleHeading: {
            keys: ["Mod+Shift+1", "Mod+Alt+1"],
            handler: ({ editor }) => {
                editor.tf.toggle.block({ type: "heading" });
            },
        },
    },
});

type ArticleRichTextEditorProps = {
    initialValue?: unknown | null;
    onChange: (value: TElement[]) => void;
};
export default function ArticleRichTextEditor({
    initialValue,
    onChange,
}: ArticleRichTextEditorProps) {
    const value = initialValue !== null && isElementList(initialValue) ? initialValue : null;
    const editor = usePlateEditor({
        value: value ?? EMPTY_EDITOR_CONTENT,
        plugins: [ParagraphPlugin, HeadingPlugin, BoldPlugin],
        override: {
            components: {
                bold: withProps(PlateLeaf, { as: "strong" }),
            },
        },
    });

    return (
        <Plate editor={editor} onChange={({ value }) => onChange?.(value)} data-registry="plate">
            <EditorContainer variant="default" className="mb-12">
                <FixedToolbar>
                    <ToolbarGroup>
                        <ToggleBlockToolbarButton blockType="heading" tooltip="Heading (⌘+⇧+1)">
                            <Heading1 />
                        </ToggleBlockToolbarButton>
                        <MarkToolbarButton nodeType={BoldPlugin.key} tooltip="Bold (⌘+B)">
                            <BoldIcon />
                        </MarkToolbarButton>
                    </ToolbarGroup>
                </FixedToolbar>
                <Editor
                    className="px-4 sm:px-4"
                    style={{ background: "#1E1E1E", color: "#f4f4f4" }}
                />
            </EditorContainer>
        </Plate>
    );
}
